import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import { useTemplate } from '../../hooks/useTemplate';
import ResizableElement from '../../components/ResizableElement';


const A4_DIMENSIONS = {
  width: '210mm',
  height: '297mm',
  padding: '20mm'
};

const InvoiceGenerator = () => {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const { template, loading, saveTemplate } = useTemplate(templateId);
  const fileInputRef = useRef(null);

  // State management for invoice data
  const [invoice, setInvoice] = useState({
    title: 'INVOICE',
    number: `INV-${new Date().getFullYear()}-${String(Math.floor(Math.random() * 1000)).padStart(3, '0')}`,
    date: new Date().toLocaleDateString(),
    dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString(),
    headerText: 'Professional Invoice',
    footerText: 'Thank you for your business',
    company: {
      name: 'Your Company Name',
      address: '123 Business Street',
      city: 'Business City, ST 12345',
      email: 'contact@company.com',
      phone: '(123) 456-7890'
    },
    client: {
      name: 'Client Name',
      address: 'Client Street Address',
      city: 'Client City, ST 12345',
      email: 'client@email.com'
    },
    items: [
      {
        id: crypto.randomUUID(),
        description: 'Service Item 1',
        quantity: 1,
        rate: 100,
        amount: 100
      }
    ],
    notes: 'Payment is due within 30 days'
  });

  const [elementPositions, setElementPositions] = useState({
    logo: { x: 40, y: 40 },
    headerText: { x: 40, y: 40 },
    title: { x: 40, y: 100 },
    companyInfo: { x: 40, y: 180 },
    invoiceDetails: { x: 400, y: 180 },
    billTo: { x: 40, y: 400 },
    items: { x: 40, y: 600 },
    notes: { x: 40, y: 800 },
    total: { x: 400, y: 800 },
    footerText: { x: 40, y: 900 }
  });

  const [elementSizes, setElementSizes] = useState({
    logo: { width: 150, height: 150 },
    headerText: { width: 300, height: 40 },
    title: { width: 300, height: 50 },
    companyInfo: { width: 300, height: 150 },
    invoiceDetails: { width: 300, height: 100 },
    billTo: { width: 300, height: 150 },
    items: { width: 800, height: 300 },
    notes: { width: 300, height: 80 },
    total: { width: 200, height: 50 },
    footerText: { width: 300, height: 40 }
  });

  const [logo, setLogo] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [templateName, setTemplateName] = useState('');

  // Load template data if editing existing template
  useEffect(() => {
    if (template) {
      setInvoice(template.invoiceData);
      setElementPositions(template.elementPositions);
      setElementSizes(template.elementSizes);
      if (template.logo) {
        setLogo(template.logo);
      }
      setTemplateName(template.name);
    }
  }, [template]);

  // Element update handlers
  const updateElementPosition = (elementId, newPosition) => {
    setElementPositions(prev => ({
      ...prev,
      [elementId]: newPosition
    }));
  };

  const updateElementSize = (elementId, newSize) => {
    setElementSizes(prev => ({
      ...prev,
      [elementId]: newSize
    }));
  };

  // Invoice item handlers
  const addItem = () => {
    setInvoice(prev => ({
      ...prev,
      items: [
        ...prev.items,
        {
          id: crypto.randomUUID(),
          description: 'New Item',
          quantity: 1,
          rate: 0,
          amount: 0
        }
      ]
    }));
  };

  const updateItem = (id, field, value) => {
    setInvoice(prev => ({
      ...prev,
      items: prev.items.map(item => {
        if (item.id === id) {
          const updatedItem = { ...item, [field]: value };
          if (field === 'quantity' || field === 'rate') {
            updatedItem.amount = updatedItem.quantity * updatedItem.rate;
          }
          return updatedItem;
        }
        return item;
      })
    }));
  };

  const removeItem = (id) => {
    if (invoice.items.length === 1) return;
    setInvoice(prev => ({
      ...prev,
      items: prev.items.filter(item => item.id !== id)
    }));
  };

  const calculateTotal = () => {
    return invoice.items.reduce((sum, item) => sum + item.amount, 0);
  };

  // Logo handlers
  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogo(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Save template handler
  const handleSave = async () => {
    if (!templateName) {
      setIsModalOpen(true);
      return;
    }

    try {
      const templateData = {
        name: templateName,
        invoiceData: invoice,
        elementPositions,
        elementSizes,
        logo
      };

      await saveTemplate(templateData);
      navigate('/InvoiceList');
    } catch (error) {
      console.error('Error saving template:', error);
      // Show error message to user
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Page Container */}
      <div className="max-w-[210mm] mx-auto p-4">
        <div className="mb-4 flex justify-between items-center">
          <button
            onClick={() => navigate('/InvoiceList')}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
          >
            ← Back to Templates
          </button>
          <div className="flex gap-2">
            <button
              onClick={() => setIsModalOpen(true)}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              Save Template
            </button>
          </div>
        </div>

        {/* A4 Page */}
        <div
          style={{
            width: A4_DIMENSIONS.width,
            minHeight: A4_DIMENSIONS.height,
            padding: A4_DIMENSIONS.padding,
            backgroundColor: 'white',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
            margin: '0 auto',
            position: 'relative'
          }}
          className="relative bg-white shadow-lg"
        >
          {/* Logo Upload */}
          <div className="absolute top-4 right-4">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleImageUpload}
              accept="image/*"
              className="hidden"
            />
            <button
              onClick={() => fileInputRef.current.click()}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              Upload Logo
            </button>
          </div>

          {/* Resizable Elements */}
          {logo && (
            <ResizableElement
              position={elementPositions.logo}
              size={elementSizes.logo}
              onPositionChange={(pos) => updateElementPosition('logo', pos)}
              onSizeChange={(size) => updateElementSize('logo', size)}
              isEditable={true}
            >
              <img src={logo} alt="Logo" className="w-full h-full object-contain" />
            </ResizableElement>
          )}

          {/* Header */}
          <ResizableElement
            value={invoice.headerText}
            onChange={(value) => setInvoice({...invoice, headerText: value})}
            position={elementPositions.headerText}
            size={elementSizes.headerText}
            onPositionChange={(pos) => updateElementPosition('headerText', pos)}
            onSizeChange={(size) => updateElementSize('headerText', size)}
            className="text-xl font-semibold text-gray-600 text-center"
          />

          {/* Title */}
          <ResizableElement
            value={invoice.title}
            onChange={(value) => setInvoice({...invoice, title: value})}
            position={elementPositions.title}
            size={elementSizes.title}
            onPositionChange={(pos) => updateElementPosition('title', pos)}
            onSizeChange={(size) => updateElementSize('title', size)}
            className="text-4xl font-bold"
          />

          {/* Company Info */}
          <ResizableElement
            value={invoice.company}
            onChange={(value) => setInvoice({...invoice, company: value})}
            position={elementPositions.companyInfo}
            size={elementSizes.companyInfo}
            onPositionChange={(pos) => updateElementPosition('companyInfo', pos)}
            onSizeChange={(size) => updateElementSize('companyInfo', size)}
            isEditable={true}
          >
            <div className="space-y-2">
              <div className="font-semibold">{invoice.company.name}</div>
              <div>{invoice.company.address}</div>
              <div>{invoice.company.city}</div>
              <div>{invoice.company.email}</div>
              <div>{invoice.company.phone}</div>
            </div>
          </ResizableElement>

          {/* Invoice Details */}
          <ResizableElement
            position={elementPositions.invoiceDetails}
            size={elementSizes.invoiceDetails}
            onPositionChange={(pos) => updateElementPosition('invoiceDetails', pos)}
            onSizeChange={(size) => updateElementSize('invoiceDetails', size)}
            isEditable={true}
          >
            <div className="text-right space-y-2">
              <div>Invoice #: {invoice.number}</div>
              <div>Date: {invoice.date}</div>
              <div>Due Date: {invoice.dueDate}</div>
            </div>
          </ResizableElement>

          {/* Bill To Section */}
          <ResizableElement
            position={elementPositions.billTo}
            size={elementSizes.billTo}
            onPositionChange={(pos) => updateElementPosition('billTo', pos)}
            onSizeChange={(size) => updateElementSize('billTo', size)}
            isEditable={true}
          >
            <div className="space-y-2">
              <h2 className="text-lg font-semibold">Bill To:</h2>
              <div className="space-y-1">
                <div className="font-semibold">{invoice.client.name}</div>
                <div>{invoice.client.address}</div>
                <div>{invoice.client.city}</div>
                <div>{invoice.client.email}</div>
              </div>
            </div>
          </ResizableElement>

          {/* Items Table */}
          <ResizableElement
            position={elementPositions.items}
            size={elementSizes.items}
            onPositionChange={(pos) => updateElementPosition('items', pos)}
            onSizeChange={(size) => updateElementSize('items', size)}
            isEditable={true}
          >
            <div className="space-y-4">
              <div className="grid grid-cols-12 gap-4 font-semibold p-2 bg-gray-50 rounded-t">
                <div className="col-span-6">Description</div>
                <div className="col-span-2 text-right">Quantity</div>
                <div className="col-span-2 text-right">Rate</div>
                <div className="col-span-2 text-right">Amount</div>
              </div>

              {invoice.items.map((item) => (
                <div key={item.id} className="grid grid-cols-12 gap-4 items-center p-2 hover:bg-gray-50">
                  <div className="col-span-6">
                    <input
                      value={item.description}
                      onChange={(e) => updateItem(item.id, 'description', e.target.value)}
                      className="w-full px-2 py-1 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div className="col-span-2">
                    <input
                      type="number"
                      value={item.quantity}
                      onChange={(e) => updateItem(item.id, 'quantity', parseFloat(e.target.value) || 0)}
                      className="w-full px-2 py-1 border rounded text-right focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div className="col-span-2">
                    <input
                      type="number"
                      value={item.rate}
                      onChange={(e) => updateItem(item.id, 'rate', parseFloat(e.target.value) || 0)}
                      className="w-full px-2 py-1 border rounded text-right focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div className="col-span-1 text-right">
                    ${item.amount.toFixed(2)}
                  </div>
                  <div className="col-span-1 text-right">
                    <button
                      onClick={() => removeItem(item.id)}
                      disabled={invoice.items.length === 1}
                      className="p-1 text-gray-500 hover:text-red-500 disabled:opacity-50"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}

              <button
                onClick={addItem}
                className="w-full px-4 py-2 text-blue-600 border border-blue-300 rounded hover:bg-blue-50 transition-colors"
              >
                <span className="flex items-center justify-center">
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Add Item
                </span>
              </button>
            </div>
          </ResizableElement>

          {/* Notes Section */}
          <ResizableElement
            value={invoice.notes}
            onChange={(value) => setInvoice({...invoice, notes: value})}
            position={elementPositions.notes}
            size={elementSizes.notes}
            onPositionChange={(pos) => updateElementPosition('notes', pos)}
            onSizeChange={(size) => updateElementSize('notes', size)}
            className="text-gray-600"
          />

          {/* Total Section */}
          <ResizableElement
            position={elementPositions.total}
            size={elementSizes.total}
            onPositionChange={(pos) => updateElementPosition('total', pos)}
            onSizeChange={(size) => updateElementSize('total', size)}
            isEditable={false}
          >
            <div className="w-full">
              <div className="flex justify-between border-t pt-2">
                <span className="font-semibold">Total:</span>
                <span className="font-semibold">${calculateTotal().toFixed(2)}</span>
              </div>
            </div>
          </ResizableElement>

          {/* Footer Text */}
          <ResizableElement
            value={invoice.footerText}
            onChange={(value) => setInvoice({...invoice, footerText: value})}
            position={elementPositions.footerText}
            size={elementSizes.footerText}
            onPositionChange={(pos) => updateElementPosition('footerText', pos)}
            onSizeChange={(size) => updateElementSize('footerText', size)}
            className="text-gray-600 text-center"
          />
        </div>
      </div>

      {/* Save Template Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg shadow-xl w-96">
            <h3 className="text-lg font-semibold mb-4">Save Template</h3>
            <input
              type="text"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              placeholder="Enter template name"
              className="w-full px-3 py-2 border rounded mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setIsModalOpen(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                disabled={!templateName.trim()}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceGenerator;